import React, { useState, useEffect } from "react";
import { Row, Col, Card, Button, Modal, Input } from "antd";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import "antd/dist/antd.css"; // Ensure Ant Design styles are imported
import ResponsiveTable from "../components/ResponsiveTable";
import LoadingSpinner from "../components/LoadingSpinner"; // Added LoadingSpinner import

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ProjectManagement = () => {
  const [data, setData] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true); // Added loading state
  const [newRecord, setNewRecord] = useState({
    projectName: "",
    createdDate: "",
    status: "Active",
    remark: "",
  });
  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState([]);

  // Fetch projects from the API
  useEffect(() => {
    let isMounted = true; // Track if the component is mounted

    const fetchProjects = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/projects/`);
        if (!response.ok) {
          const errorText = await response.text();
          console.error("Failed to fetch projects:", errorText);
          throw new Error("Failed to fetch projects");
        }

        const projects = await response.json();
        if (isMounted) {
          setData(
            projects.map((project) => ({
              key: project.project_id,
              projectId: project.project_id,
              projectName: project.project_name,
              createdDate: project.created_date,
              status: project.status
                ? project.status.charAt(0).toUpperCase() + project.status.slice(1) // Capitalize status
                : "Unknown", // Handle undefined or null status
              remark: project.remarks || "No remarks", // Handle undefined or null remarks
            }))
          );
        }
      } catch (error) {
        if (isMounted) console.error("Error fetching projects:", error);
      } finally {
        if (isMounted) setIsInitialLoad(false); // Turn off loading spinner
      }
    };

    fetchProjects(); // Initial fetch

    const handleFocus = () => {
      if (isMounted) fetchProjects(); // Refetch projects when the page is focused
    };

    window.addEventListener("focus", handleFocus);
    const interval = setInterval(fetchProjects, 30000); // Auto-refresh every 30 seconds
    return () => {
      isMounted = false; // Cleanup: Mark the component as unmounted
      window.removeEventListener("focus", handleFocus);
      clearInterval(interval); // Cleanup interval on component unmount
    };
  }, []);

  // Filter data based on search text
  useEffect(() => {
    setFilteredData(
      data.filter((item) =>
        Object.values(item).some((value) =>
          String(value).toLowerCase().includes(searchText.toLowerCase())
        )
      )
    );
  }, [searchText, data]);

  // Add a new project
  const addNewRecord = async () => {
    try {
      const requestBody = {
        project_name: newRecord.projectName,
        status: newRecord.status.toLowerCase(), // Convert status to lowercase as per the example
        remarks: newRecord.remark,
      };

      const response = await fetch(`${API_BASE_URL}/projects/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Failed to add project:", errorText);
        throw new Error("Failed to add project");
      }

      const project = await response.json();

      // Ensure the key is set correctly
      setData((prevData) => [
        ...prevData,
        {
          key: project.project_id, // Use project_id as the key
          projectId: project.project_id,
          projectName: project.project_name,
          createdDate: project.created_date,
          status: project.status.charAt(0).toUpperCase() + project.status.slice(1),
          remark: project.remarks,
        },
      ]);

      setIsAdding(false);
      setNewRecord({
        projectName: "",
        createdDate: "",
        status: "Active",
        remark: "",
      });
    } catch (error) {
      console.error("Error adding project:", error);
    }
  };

  // Update an existing project
  const saveEdit = async () => {
    try {
      const requestBody = {
        project_name: editingRecord.projectName,
        status: editingRecord.status.toLowerCase(), // Convert status to lowercase
        remarks: editingRecord.remark,
      };

      const response = await fetch(`${API_BASE_URL}/projects/${editingRecord.projectId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Failed to update project:", errorText);
        throw new Error("Failed to update project");
      }

      // Update the project locally without requiring a page refresh
      setData((prevData) =>
        prevData.map((item) =>
          item.projectId === editingRecord.projectId
            ? {
              ...item,
              projectName: editingRecord.projectName,
              status: editingRecord.status.charAt(0).toUpperCase() + editingRecord.status.slice(1), // Capitalize status
              remark: editingRecord.remark,
            }
            : item
        )
      );

      setIsEditing(false);
      setEditingRecord(null);
    } catch (error) {
      console.error("Error updating project:", error);
      alert("An error occurred while updating the project. Please try again.");
    }
  };

  // Delete a project
  const deleteRow = async (key) => {
    try {
      const response = await fetch(`${API_BASE_URL}/projects/${key}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Failed to delete project:", errorText);
        throw new Error("Failed to delete project");
      }

      // Update the data state to remove the deleted project
      setData((prevData) => prevData.filter((item) => item.projectId !== key));
    } catch (error) {
      console.error("Error deleting project:", error);
    }
  };

  const toggleStatus = async (projectId) => {
    const record = data.find((item) => item.projectId === projectId);
    if (record) {
      try {
        const updatedStatus = record.status === "Active" ? "inactive" : "active";

        const requestBody = { status: updatedStatus };

        const response = await fetch(`${API_BASE_URL}/project/projects/${projectId}/status`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error("Failed to update project status:", errorText);
          throw new Error("Failed to update project status");
        }

        // Update the status locally without requiring a page refresh
        setData((prevData) =>
          prevData.map((item) =>
            item.projectId === projectId
              ? { ...item, status: updatedStatus.charAt(0).toUpperCase() + updatedStatus.slice(1) }
              : item
          )
        );
      } catch (error) {
        console.error("Error updating project status:", error);
      }
    } else {
      console.error("Project not found for projectId:", projectId);
    }
  };

  const editRow = (record) => {
    setIsEditing(true);
    setEditingRecord({ ...record });
  };

  const columns = [
    {
      title: "PROJECT ID",
      dataIndex: "projectId",
      key: "projectId",
      sorter: (a, b) => a.projectId.localeCompare(b.projectId), // Sorting by string comparison
    },
    {
      title: "PROJECT NAME",
      dataIndex: "projectName",
      key: "projectName",
      sorter: (a, b) => a.projectName.localeCompare(b.projectName), // Sorting by string comparison
    },
    {
      title: "CREATED DATE",
      dataIndex: "createdDate",
      key: "createdDate",
      sorter: (a, b) => new Date(a.createdDate) - new Date(b.createdDate), // Sorting by date
    },
    {
      title: "STATUS",
      dataIndex: "status",
      key: "status",
      render: (status, record) => (
        <Button
          type="primary"
          style={{
            backgroundColor: status === "Active" ? "#1890ff" : "#d9d9d9", // Blue for "Active", default for others
            borderColor: status === "Active" ? "#1890ff" : "#d9d9d9",
            color: status === "Active" ? "#fff" : "#000",
          }}
          onClick={() => toggleStatus(record.projectId)}
        >
          {status}
        </Button>
      ),
    },
    {
      title: "REMARK",
      dataIndex: "remark",
      key: "remark",
    },
    {
      title: "ACTION",
      key: "action",
      render: (_, record) => (
        <>
          <Button
            icon={<EditOutlined style={{ color: "#1890ff" }} />}
            onClick={() => editRow(record)}
            style={{ marginRight: 8, color: "#1890ff", borderColor: "#1890ff" }}
          >
            Edit
          </Button>
          <Button
            onClick={() => deleteRow(record.key)}
            danger
          >
            ❌ Delete
          </Button>
        </>
      ),
    },
  ];

  return (
    <div className="project-management">
      <Row gutter={[24, 0]}>
        <Col xs="24" xl={24}>
          <Card
            bordered={false}
            className="criclebox tablespace mb-24"
            title="Project Management Table" // Added title
            extra={
              <>
                <Input
                  placeholder="Search..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ width: 200, marginRight: 8 }}
                />
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setIsAdding(true)}
                >
                  Add Project
                </Button>
              </>
            }
          >
            {isInitialLoad ? (
              <LoadingSpinner text="Loading records..." />
            ) : (
              <div className="table-responsive">
                <ResponsiveTable
                  columns={columns}
                  dataSource={filteredData}
                  pagination={{ pageSize: 10 }}
                  scroll={{ x: 1000 }}
                  className="ant-border-space"
                />
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Edit Modal */}
      <Modal
        title="Edit Project"
        visible={isEditing}
        onOk={saveEdit}
        onCancel={() => {
          setIsEditing(false);
          setEditingRecord(null);
        }}
      >
        <Input
          placeholder="Project ID"
          value={editingRecord?.projectId}
          onChange={(e) =>
            setEditingRecord({ ...editingRecord, projectId: e.target.value })
          }
          style={{ marginBottom: 8 }}
        />
        <Input
          placeholder="Project Name"
          value={editingRecord?.projectName}
          onChange={(e) =>
            setEditingRecord({ ...editingRecord, projectName: e.target.value })
          }
          style={{ marginBottom: 8 }}
        />
        <Input
          placeholder="Created Date"
          value={editingRecord?.createdDate}
          onChange={(e) =>
            setEditingRecord({ ...editingRecord, createdDate: e.target.value })
          }
          style={{ marginBottom: 8 }}
        />
        <Input
          placeholder="Remark"
          value={editingRecord?.remark}
          onChange={(e) =>
            setEditingRecord({ ...editingRecord, remark: e.target.value })
          }
        />
      </Modal>

      {/* Add Modal */}
      <Modal
        title="Add New Project"
        visible={isAdding}
        onOk={addNewRecord}
        onCancel={() => setIsAdding(false)}
      >
        <Input
          placeholder="Project Name"
          value={newRecord.projectName}
          onChange={(e) =>
            setNewRecord({ ...newRecord, projectName: e.target.value })
          }
          style={{ marginBottom: 8 }}
        />
        <Input
          placeholder="Created Date"
          value={newRecord.createdDate}
          onChange={(e) =>
            setNewRecord({ ...newRecord, createdDate: e.target.value })
          }
          style={{ marginBottom: 8 }}
        />
        <Input
          placeholder="Remark"
          value={newRecord.remark}
          onChange={(e) =>
            setNewRecord({ ...newRecord, remark: e.target.value })
          }
        />
      </Modal>
    </div>
  );
};

export default ProjectManagement;